'use client';

import { Fragment, useState } from 'react';
import styles from './VendorSection.module.scss';
import { gaClick } from '@/src/helpers/gtag';
import { useAOS } from '@/src/lib/hooks/useAOS';
import { VendorPackRequestForm } from './VendorPackRequestForm';

export type VendorBlock = { title: string; description: string };

export type VendorSectionProps = {
  eyebrow?: string;
  title?: string;
  subheading?: string;
  intro?: string;
  blocks?: VendorBlock[];
  ctaTitle?: string;
  ctaDescription?: string;
  ctaLabel?: string;
};

const DEFAULT_BLOCKS: VendorBlock[] = [
  {
    title: 'Engagement au forfait',
    description: 'Un périmètre, un prix, une date. Nous portons le risque de delivery.',
  },
  {
    title: 'Confidentialité et RGPD',
    description: 'NDA systématique et traitement des données conforme au RGPD.',
  },
  {
    title: 'Sécurité',
    description: 'Pratiques de développement sécurisé et accès encadrés à vos environnements.',
  },
  {
    title: 'Cadre fournisseur',
    description: 'Documents administratifs, assurances et référencement fournisseur à jour.',
  },
  {
    title: 'Propriété intellectuelle',
    description: 'Le code livré vous appartient, sans licence cachée ni dépendance.',
  },
  {
    title: 'Recette et transfert',
    description: 'Recette formalisée puis transfert complet de compétences à vos équipes.',
  },
];

const renderTitle = (text: string) => {
  const parts = text.split('*');
  return parts.map((part, i) =>
    i % 2 === 1 ? <em key={i}>{part}</em> : <Fragment key={i}>{part}</Fragment>
  );
};

export function VendorSection({
  eyebrow = 'CADRE CONTRACTUEL ET OPÉRATIONNEL',
  title = 'Travailler avec Averiane',
  subheading,
  intro,
  blocks = DEFAULT_BLOCKS,
  ctaTitle = 'Demander notre Vendor Pack',
  ctaDescription,
  ctaLabel = 'Demander notre Vendor Pack',
  lang,
}: VendorSectionProps & { lang?: string }) {
  useAOS();

  const isEn = lang === 'en';
  const [formOpen, setFormOpen] = useState(false);

  const resolvedCtaDescription =
    ctaDescription ??
    (isEn
      ? 'A single document summarising our contractual, security and operational framework for procurement and vendor teams.'
      : 'Un document unique résumant notre cadre contractuel, sécurité et opérationnel pour les équipes achats et fournisseurs.');

  const formId = 'vendor-pack-form';

  return (
    <section id="travailler-avec-averiane" className={styles.root} aria-labelledby="vendor-title">
      <div className={styles.inner}>
        <div className={styles.header} data-aos="fade-up" data-aos-duration="700">
          {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
          <h2 id="vendor-title" className={styles.title}>
            {renderTitle(title)}
          </h2>
          {subheading && <p className={styles.subheading}>{renderTitle(subheading)}</p>}
          {intro && <p className={styles.intro}>{intro}</p>}
        </div>

        {blocks.length > 0 && (
          <ul
            className={styles.grid}
            data-aos="fade-up"
            data-aos-duration="800"
            data-aos-delay="100"
          >
            {blocks.map((block) => (
              <li key={block.title} className={styles.block}>
                <h3 className={styles.blockTitle}>{block.title}</h3>
                <p className={styles.blockDesc}>{block.description}</p>
              </li>
            ))}
          </ul>
        )}

        <div className={styles.cta} data-aos="fade-up" data-aos-duration="800" data-aos-delay="150">
          <div className={styles.ctaText}>
            <h3 className={styles.ctaTitle}>{ctaTitle}</h3>
            {resolvedCtaDescription && (
              <p className={styles.ctaDescription}>{resolvedCtaDescription}</p>
            )}
          </div>

          <button
            type="button"
            className={styles.ctaButton}
            aria-expanded={formOpen}
            aria-controls={formId}
            onClick={() => {
              const next = !formOpen;
              setFormOpen(next);
              gaClick('vendor_pack_cta_toggle', { open: next });
            }}
          >
            {ctaLabel}
          </button>

          {formOpen && (
            <div className={styles.formWrap} id={formId}>
              <VendorPackRequestForm lang={lang} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
